// Consent Banner Component
// Displays privacy consent options to users

import React, { useState } from 'react';
import { useConsent } from '../../context/ConsentContext';
import { useDarkMode } from '../../context/DarkModeContext';
import { createLogger } from '../../utils/logger';
import { IoCloseOutline, IoShieldCheckmarkOutline, IoSettingsOutline } from 'react-icons/io5';
import type { ConsentPreferences } from '../../types/privacy';
import { DEFAULT_CONSENT } from '../../constants/privacy';
import { ConsentControls } from './ConsentControls';

const logger = createLogger({ module: 'ConsentBanner' });

interface ConsentBannerProps {
  className?: string;
  position?: 'top' | 'bottom';
  showAdvanced?: boolean;
}

export const ConsentBanner: React.FC<ConsentBannerProps> = ({
  className = '',
  position = 'bottom',
  showAdvanced = false
}) => {
  const { showConsentBanner, updateConsent, dismissBanner } = useConsent();
  const { darkMode } = useDarkMode();
  const [showDetails, setShowDetails] = useState(showAdvanced);
  const [customConsent, setCustomConsent] = useState<ConsentPreferences>(DEFAULT_CONSENT);
  const [isUpdating, setIsUpdating] = useState(false);

  if (!showConsentBanner) {
    return null;
  }

  const handleAcceptAll = async () => {
    setIsUpdating(true);
    try {
      const allConsent: ConsentPreferences = {
        necessary: true,
        analytics: true,
        functional: true,
        performance: true,
        ai_processing: true
      };
      
      const success = await updateConsent(allConsent);
      if (success) {
        logger.info('User accepted all consent categories');
      } else {
        logger.error('Failed to save consent preferences');
      }
    } catch (error) {
      logger.error('Error accepting all consent', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRejectAll = async () => {
    setIsUpdating(true);
    try {
      const minimalConsent: ConsentPreferences = {
        necessary: true,
        analytics: false,
        functional: false,
        performance: false,
        ai_processing: false
      };
      
      const success = await updateConsent(minimalConsent);
      if (success) {
        logger.info('User rejected optional consent categories');
      } else {
        logger.error('Failed to save consent preferences');
      }
    } catch (error) {
      logger.error('Error rejecting consent', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCustomSave = async () => {
    setIsUpdating(true);
    try {
      const success = await updateConsent(customConsent);
      if (success) {
        logger.info('User saved custom consent preferences');
      } else {
        logger.error('Failed to save custom consent preferences');
      }
    } catch (error) {
      logger.error('Error saving custom consent', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleConsentToggle = (category: keyof ConsentPreferences) => {
    if (category === 'necessary') return; // Cannot disable necessary consent
    
    setCustomConsent(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const positionClasses = position === 'top' 
    ? 'top-0' 
    : 'bottom-0';

  return (
    <div className={`fixed left-0 right-0 ${positionClasses} z-50 ${className}`}>
      <div className={`mx-4 mb-4 rounded-lg shadow-lg border ${
        darkMode 
          ? 'bg-gray-800 border-gray-700 text-white' 
          : 'bg-white border-gray-200 text-gray-900'
      }`}>
        <div className="p-4">
          {/* Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center">
              <IoShieldCheckmarkOutline className="w-6 h-6 text-blue-500 mr-2" />
              <h3 className="text-lg font-semibold">Privacy Preferences</h3>
            </div>
            <button
              onClick={dismissBanner}
              className={`p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 ${
                darkMode ? 'text-gray-400' : 'text-gray-500'
              }`}
              aria-label="Dismiss banner"
            >
              <IoCloseOutline className="w-5 h-5" />
            </button>
          </div>

          {/* Main content */}
          <div className="mb-4">
            <p className={`text-sm mb-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              We use cookies and similar technologies to enhance your experience, analyze usage, and provide personalized content. 
              Choose your preferences below.
            </p>

            {!showDetails ? (
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={handleAcceptAll}
                  disabled={isUpdating}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUpdating ? 'Saving...' : 'Accept All'}
                </button>
                <button
                  onClick={handleRejectAll}
                  disabled={isUpdating}
                  className={`px-4 py-2 rounded-md border ${
                    darkMode 
                      ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {isUpdating ? 'Saving...' : 'Reject Optional'}
                </button>
                <button
                  onClick={() => setShowDetails(true)}
                  className={`px-4 py-2 rounded-md border ${
                    darkMode 
                      ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  } flex items-center`}
                >
                  <IoSettingsOutline className="w-4 h-4 mr-1" />
                  Customize
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                <ConsentControls
                  consent={customConsent}
                  onConsentChange={handleConsentToggle}
                  disabled={isUpdating}
                  showDescriptions={true}
                  showExamples={true}
                />

                <div className="flex flex-wrap gap-2 pt-2">
                  <button
                    onClick={handleCustomSave}
                    disabled={isUpdating}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isUpdating ? 'Saving...' : 'Save Preferences'}
                  </button>
                  <button
                    onClick={() => setShowDetails(false)}
                    className={`px-4 py-2 rounded-md border ${
                      darkMode 
                        ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Back
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <p>
              You can change these preferences at any time in your{' '}
              <a href="/settings" className="text-blue-500 hover:underline">
                privacy settings
              </a>
              . For more information, see our{' '}
              <a href="/privacy" className="text-blue-500 hover:underline">
                Privacy Policy
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}; 